/**
 * «Ціни РДН» — індекси цін із навігацією, як на сайті Оператора ринку.
 *
 * ЯК ЦЕ ЗРОБЛЕНО В НИХ (розібрав розмітку oree.com.ua):
 *   · перемикач періоду — це radio-group із двох кнопок, value="day" і
 *     value="month", тобто сегментований контрол, а не вкладки;
 *   · датапікер міняє ФОРМАТ разом із періодом: 29.09.2026 для доби і
 *     09.2026 для місяця — у розмітці це два різні елементи;
 *   · межі вибору приходять із даних: data-date-start-date="27.08.2019",
 *     data-date-end-date="29.09.2026". Дати поза наявними торгами обрати
 *     не можна;
 *   · картки індексів показують значення, стрілку й відсоток зміни до
 *     попереднього періоду (у них класи price-green / price-red).
 *
 * ЩО ВЗЯТО, А ЩО НІ. Поведінка й склад блоку — їхні. Оформлення наше:
 * контроли 32/radius 8 з обводкою --line-strong (§6), плитка .tile, токени.
 * Колір зміни — за догмою §3: зростання --ok, падіння --warn. Червоного тут
 * немає: падіння ціни не є збоєм системи, а в них саме червоний.
 *
 * ДАНІ. GET /market/dam-summary?date=… — той самий ендпойнт, що живить
 * розділ «Ринок, ціни». Він приймає дату й повертає, окрім зон, ще й
 * dataRange {min,max} — це і є межі для датапікера, як у них.
 *
 * ЗА МІСЯЦЬ показуємо ті самі чотири зони з відсотком зміни — як у них.
 * Бекенд зон за місяць не віддає (лише tiles.month одним числом), тож вони
 * рахуються з погодинних торгів — див. блок нижче. Для відсотка потрібен ще
 * і попередній місяць, тож він довантажується другим кроком.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useEffect, useMemo, useState } from 'react';
import { Calendar, Moon, Sun, TrendingDown, TrendingUp } from 'lucide-react';
import { MonthField } from '@/components/base/UaCalendar';
import AnimatedNumber from '@/components/base/AnimatedNumber';
import { API_BASE_URL, authFetch } from '@/services/api';

interface Zone { wavg: number | null; prevWavg: number | null; changePct: number | null }
interface MonthZone extends Zone { ym: string; prevYm: string }
interface Summary {
  tradeDate?: string;
  dataRange?: { min: string | null; max: string | null };
  tiles?: {
    day: Zone; day_8_23: Zone; night: Zone; peak: Zone; semipeak: Zone;
    month: MonthZone;
  };
}

type Period = 'day' | 'month';

const nf = (d = 2) =>
  new Intl.NumberFormat(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/* ── місячні зони ──────────────────────────────────────────────────────────
   На сайті Оператора ринку при перемиканні на місяць змінюються ВСІ чотири
   зони. Наш бекенд так не вміє: /market/dam-summary віддає зони лише за добу,
   а за місяць — одне загальне середньозважене (tiles.month).

   Тому рахуємо самі з /market/dam-hourly?date=… по кожному дню місяця.
   Це не наближення: зонний індекс і є середньозваженою ціною за обсягом,
   тобто Σ(ціна×обсяг)/Σ(обсяг) по годинах зони — рівно те, що робимо тут.

   ЦІНА ПИТАННЯ — до 31 запиту на місяць. Тому: рахуємо ЛИШЕ коли користувач
   справді перемкнувся на місяць, тримаємо не більше 5 запитів одночасно й
   кешуємо результат — повторний вибір того самого місяця безкоштовний. */

interface HourRow { hour: number; price: number | null; volume: number | null }

type ZoneKey = 'day_8_23' | 'night' | 'peak' | 'semipeak';

/** Межі зон. Година 0 трактується як 24 — нумерація джерела різниться. */
const inZone = (h0: number, z: ZoneKey) => {
  const h = h0 === 0 ? 24 : h0;
  if (z === 'day_8_23') return h >= 8 && h <= 23;
  if (z === 'night') return (h >= 1 && h <= 7) || h === 24;
  if (z === 'peak') return h >= 18 && h <= 23;
  return h >= 8 && h <= 17;
};

interface MonthZones {
  zones: Record<ZoneKey, number | null>;
  /** Скільки діб справді дали дані — показуємо користувачу як покриття. */
  days: number;
}

const monthCache = new Map<string, MonthZones>();

/* ── методика: СЕРЕДНЬОАРИФМЕТИЧНА, як на сайті Оператора ринку ─────────────
   У підказці до блоку «Індекси цін» на oree.com.ua написано прямо:
   «Середньоарифметичне значення по годинам». Перевірено на даних 30.09.2026 —
   їхні 5657.02 / 5228.23 / 10533.73 / 2731.00 збігаються з арифметичним
   середнім до копійки.

   Наш бекенд рахує ці ж зони СЕРЕДНЬОЗВАЖЕНО за обсягом: у коді стоїть
   позначка, що так зробили 27.08.2026 на вимогу клієнта. Для 30.09 це дає
   6338.08 / 5348.04 / 10720.05 / 3181.72 — інші числа.

   Тому на ЦІЙ сторінці зони рахуємо самі, арифметично, з погодинних торгів.
   Бекенд і розділ «Ринок, ціни» лишаються незмінними: домовленість про
   середньозважену там чинна, і скасовувати її не наша справа. */

const dayCache = new Map<string, HourRow[]>();

async function fetchDayHours(d: string): Promise<HourRow[]> {
  const hit = dayCache.get(d);
  if (hit) return hit;
  try {
    const r = await authFetch(`${API_BASE_URL}/market/dam-hourly?date=${d}`);
    const j = r.ok ? await r.json() : null;
    const rows = (j?.hours ?? []) as HourRow[];
    dayCache.set(d, rows);
    return rows;
  } catch {
    return [];
  }
}

/** Арифметичне середнє по зонах для набору діб. */
function zonesOf(perDay: HourRow[][]): MonthZones {
  const acc: Record<ZoneKey, { sum: number; n: number }> = {
    day_8_23: { sum: 0, n: 0 }, night: { sum: 0, n: 0 },
    peak: { sum: 0, n: 0 }, semipeak: { sum: 0, n: 0 },
  };
  let days = 0;
  for (const rows of perDay) {
    if (!rows.length) continue;
    days += 1;
    for (const h of rows) {
      if (h.price == null) continue;
      (['day_8_23', 'night', 'peak', 'semipeak'] as ZoneKey[]).forEach((z) => {
        if (inZone(h.hour, z)) { acc[z].sum += h.price!; acc[z].n += 1; }
      });
    }
  }
  return {
    zones: {
      day_8_23: acc.day_8_23.n ? acc.day_8_23.sum / acc.day_8_23.n : null,
      night: acc.night.n ? acc.night.sum / acc.night.n : null,
      peak: acc.peak.n ? acc.peak.sum / acc.peak.n : null,
      semipeak: acc.semipeak.n ? acc.semipeak.sum / acc.semipeak.n : null,
    },
    days,
  };
}

/** Зони за одну добу (2 запити на пару «доба + попередня»). */
async function zonesForDay(d: string): Promise<MonthZones> {
  return zonesOf([await fetchDayHours(d)]);
}

/** Попередня доба в ISO. */
function prevDay(d: string): string {
  const [y, m, dd] = d.split('-').map(Number);
  const t = new Date(y, m - 1, dd - 1);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** Попередній місяць у форматі YYYY-MM. */
function prevYm(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function daysOfMonth(ym: string): string[] {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return Array.from({ length: last }, (_, i) =>
    `${ym}-${String(i + 1).padStart(2, '0')}`);
}

async function fetchMonthZones(ym: string, maxDate?: string | null) {
  const cached = monthCache.get(ym);
  if (cached) return cached;

  // Майбутні дні не запитуємо: торгів по них немає, і це були б порожні
  // відповіді замість даних.
  const days = daysOfMonth(ym).filter((d) => !maxDate || d <= maxDate);

  // Не більше 5 запитів одночасно: місяць — це до 31 доби, і вивалювати їх
  // разом на спільний прод-бекенд не варто.
  const LIMIT = 5;
  let i = 0;
  const perDay: HourRow[][] = [];
  await Promise.all(Array.from({ length: Math.min(LIMIT, days.length) }, async () => {
    while (i < days.length) {
      perDay.push(await fetchDayHours(days[i++]));
    }
  }));

  const out = zonesOf(perDay);
  monthCache.set(ym, out);
  return out;
}

const CTRL: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 32, borderRadius: 8, padding: '0 12px',
  background: 'transparent', border: '1px solid var(--line-strong)',
  color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap',
};

/**
 * Картка індексу: значення, одиниця, стрілка й відсоток до попереднього.
 *
 * Значення й відсоток НАБИРАЮТЬСЯ лічильником — той самий ефект, що на KPI
 * «Прогнозу» й у блоці періоду вище. `id` прив'язаний до зони, щоб анімація
 * пережила перемонтування картки при зміні періоду.
 */
function IndexCard({ label, Icon, zone, hint, id }: {
  label: string; Icon: typeof Sun; zone?: Zone; hint?: string; id?: string;
}) {
  const ch = zone?.changePct ?? null;
  // Догма §3: зростання --ok, падіння --warn, немає порівняння — сірий.
  const tone = ch == null ? 'var(--text-dim)' : ch >= 0 ? 'var(--ok)' : 'var(--warn)';
  const Arrow = ch == null ? null : ch >= 0 ? TrendingUp : TrendingDown;
  return (
    <div style={{ minWidth: 0 }}>
      <div className="flex items-center" style={{ gap: 6 }}>
        <Icon size={14} strokeWidth={1.9} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <span className="truncate" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <div className="flex items-baseline" style={{ gap: 5, marginTop: 4 }}>
        {zone?.wavg == null ? (
          <span className="font-display tabular-nums" style={{
            fontSize: 26, fontWeight: 400, color: 'var(--text)', lineHeight: 1.1,
          }}>—</span>
        ) : (
          <AnimatedNumber
            id={id}
            value={zone.wavg}
            format={(v) => nf(2).format(v)}
            className="font-display tabular-nums"
            style={{ fontSize: 26, fontWeight: 400, color: 'var(--text)', lineHeight: 1.1 }}
          />
        )}
        <span style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{i18n.t('ak_home_uah_mwh')}</span>
      </div>
      <div className="flex items-center" style={{ gap: 4, marginTop: 3, minHeight: 15 }}>
        {Arrow && <Arrow size={12} strokeWidth={2.2} style={{ color: tone, flexShrink: 0 }} />}
        {ch == null ? (
          <span className="tabular-nums" style={{ fontSize: 11, color: tone }}>
            {hint ?? i18n.t('ak_home_no_comparison')}
          </span>
        ) : (
          // Знак «+» окремо від числа: лічильник анімує саму величину, а не
          // рядок, тож знак не має в ній брати участі.
          <span className="flex items-center tabular-nums" style={{ fontSize: 11, color: tone }}>
            {ch > 0 ? '+' : ''}
            <AnimatedNumber
              id={id ? `${id}-pct` : undefined}
              value={ch}
              format={(v) => `${nf(2).format(v)}%`}
              className="tabular-nums"
              style={{ fontSize: 11, color: tone }}
            />
          </span>
        )}
      </div>
    </div>
  );
}

export default function RdnIndexBlock() {
  useTranslation();
  const [period, setPeriod] = useState<Period>('day');
  // СЬОГОДНІ, а не остання торгова доба. РДН — ринок «на добу наперед», тож
  // остання доба в даних це ЗАВТРА, і блок відкривався на завтрашніх цінах.
  const [date, setDate] = useState<string>(todayIso);
  const [s, setS] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  // Зведення тягнемо лише заради меж датапікера (dataRange). Самі зони
  // рахуємо окремо: бекенд віддає їх середньозваженими, а тут потрібні
  // середньоарифметичні — як на сайті Оператора ринку.
  useEffect(() => {
    let alive = true;
    authFetch(`${API_BASE_URL}/market/dam-summary`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Summary | null) => { if (alive) setS(d); })
      .catch(() => { if (alive) setS(null); });
    return () => { alive = false; };
  }, []);

  const range = s?.dataRange;

  // Добові зони: обрана доба і попередня — для відсотка зміни.
  const [dz, setDz] = useState<MonthZones | null>(null);
  const [dzPrev, setDzPrev] = useState<MonthZones | null>(null);

  useEffect(() => {
    if (period !== 'day' || !date) return;
    let alive = true;
    setLoading(true);
    setDz(null);
    setDzPrev(null);
    (async () => {
      const cur = await zonesForDay(date);
      if (!alive) return;
      setDz(cur);
      const prev = await zonesForDay(prevDay(date));
      if (!alive) return;
      setDzPrev(prev);
    })().finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, date]);

  // Місячні зони рахуємо лише в режимі місяця й лише один раз на місяць.
  const [mz, setMz] = useState<MonthZones | null>(null);
  const [mzPrev, setMzPrev] = useState<MonthZones | null>(null);
  const [mzLoading, setMzLoading] = useState(false);
  const ym = date ? date.slice(0, 7) : '';

  useEffect(() => {
    if (period !== 'month' || !ym) return;
    let alive = true;
    setMz(monthCache.get(ym) ?? null);
    setMzPrev(monthCache.get(prevYm(ym)) ?? null);
    if (monthCache.has(ym) && monthCache.has(prevYm(ym))) return;

    setMzLoading(true);
    // Спершу обраний місяць — щоб числа з'явились якомога раніше, — і лише
    // потім попередній, потрібний тільки для відсотка зміни. Послідовно, а не
    // разом: два місяці паралельно це до 60 одночасних запитів до спільного
    // прод-бекенда.
    (async () => {
      const cur = await fetchMonthZones(ym, range?.max ?? null);
      if (!alive) return;
      setMz(cur);
      const prev = await fetchMonthZones(prevYm(ym), range?.max ?? null);
      if (!alive) return;
      setMzPrev(prev);
    })().finally(() => { if (alive) setMzLoading(false); });

    return () => { alive = false; };
  }, [period, ym, range?.max]);

  /**
   * Зона для картки. Обидва режими рахуються однаково — середньоарифметично
   * з погодинних торгів; різниця лише в наборі діб (одна проти місяця).
   * Відсоток — до попереднього періоду, порахованого так само.
   */
  const zoneOf = (k: ZoneKey): Zone | undefined => {
    const cur = period === 'day' ? dz : mz;
    const prv = period === 'day' ? dzPrev : mzPrev;
    if (!cur) return undefined;
    const v = cur.zones[k];
    const p = prv?.zones[k] ?? null;
    return {
      wavg: v,
      prevWavg: p,
      changePct: v != null && p != null && p !== 0 ? ((v - p) / p) * 100 : null,
    };
  };

  // У режимі місяця датапікер працює з YYYY-MM — як у них окремий елемент із
  // форматом 09.2026. Межі теж урізаємо до місяця.
  const monthValue = date ? date.slice(0, 7) : '';
  const monthMin = range?.min ? range.min.slice(0, 7) : undefined;
  const monthMax = range?.max ? range.max.slice(0, 7) : undefined;

  /**
   * Сусідній період у межах наявних даних, або null на краю.
   *
   * Межі ті самі, що й у датапікера (dataRange з бекенда), тож стрілка не
   * виводить на добу без торгів — на краю вона просто погашена.
   */
  const step = (delta: number): string | null => {
    if (period === 'month') {
      const [y, m] = (date || todayIso()).slice(0, 7).split('-').map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (monthMin && ym < monthMin) return null;
      if (monthMax && ym > monthMax) return null;
      return `${ym}-01`;
    }
    const [y, m, dd] = (date || todayIso()).split('-').map(Number);
    const t = new Date(y, m - 1, dd + delta);
    const next = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    if (range?.min && next < range.min) return null;
    if (range?.max && next > range.max) return null;
    return next;
  };

  const prevStep = step(-1);
  const nextStep = step(1);

  return (
    <div style={{ display: 'grid', gap: 12 }}>

      {/* НАВІГАЦІЯ — ПОЗА ПЛИТКОЮ, одразу під заголовком «Ціни РДН»: так само,
          як у верхньому блоці сторінки, де дата й період стоять над плиткою, а
          не всередині неї. Склад контролів їхній (oree), вигляд наш. */}
      {/* Заголовок, навігація й статус — в ОДНОМУ рядку, низ по одній лінії
          (items-end): заголовок ліворуч, навігація праворуч, статус — ліворуч від неї.
          На вузьких екранах стовпчик: заголовок над навігацією. */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] items-end" style={{ gap: 12, marginTop: 20 }}>
        <div className="font-display" style={{
          fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
          letterSpacing: '.06em', textTransform: 'uppercase', lineHeight: 1,
        }}>
          {i18n.t('ak_home_dam_prices')}
        </div>
      <div className="flex flex-wrap items-center justify-end md:col-start-3 md:row-start-1" style={{ gap: 8 }}>
        {/* Перемикач «За добу / За місяць» — ліворуч у групі, календар із кроками
            посідає його колишнє місце (правий край). */}
        <div className="flex items-center" style={{ gap: 4, marginRight: 4 }}>
          {(['day', 'month'] as Period[]).map((p) => {
            const on = period === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                style={{
                  ...CTRL,
                  color: on ? 'var(--text)' : 'var(--text-muted)',
                  background: on ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent',
                }}
              >
                {p === 'day' ? i18n.t('ak_home_per_day') : i18n.t('ak_home_per_month')}
              </button>
            );
          })}
        </div>

        <div className="flex items-center" style={{
          // Ширина стала: у режимі місяця значення коротше (09.2026 проти
          // 30.09.2026), і без фіксації сусідні кнопки стрибали при кожному
          // перемиканні.
          gap: 8, height: 32, width: 176, padding: '0 10px', borderRadius: 8, flexShrink: 0,
          background: 'var(--surface-2)', border: '1px solid var(--line)',
        }}>
          <Calendar size={14} strokeWidth={2} style={{ color: 'var(--text-dim)' }} />
          {period === 'day' ? (
            <input
              type="date"
              value={date}
              min={range?.min ?? undefined}
              max={range?.max ?? undefined}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="outline-none cursor-pointer tabular-nums"
              style={{ background: 'transparent', border: 'none', color: 'var(--text)', fontSize: 12.5, fontWeight: 600, width: '100%' }}
            />
          ) : (
            /* Назву місяця малюємо самі: нативний type=month показує її мовою
               інтерфейсу браузера. Рамку й значок дає обгортка, тож тут поле
               без власної геометрії. */
            <MonthField
              value={monthValue}
              min={monthMin}
              max={monthMax}
              onChange={(ym) => setDate(`${ym}-01`)}
              icon={false}
              width="100%"
              style={{
                height: 'auto', padding: 0, borderRadius: 0,
                background: 'transparent', border: 'none',
                color: 'var(--text)', fontSize: 12.5, fontWeight: 600,
              }}
            />
          )}
        </div>

        {/* Крок назад і вперед — окремими кнопками поруч із полем, як у
            верхньому блоці: 32/radius 8, лише символ. */}
        <button
          type="button"
          disabled={!prevStep}
          onClick={() => prevStep && setDate(prevStep)}
          style={{ ...CTRL, width: 32, padding: 0, opacity: prevStep ? 1 : 0.4 }}
          title={period === 'month' ? i18n.t('ak_home_previous_month') : i18n.t('ak_home_previous_day')}
        >
          ‹
        </button>
        <button
          type="button"
          disabled={!nextStep}
          onClick={() => nextStep && setDate(nextStep)}
          style={{ ...CTRL, width: 32, padding: 0, opacity: nextStep ? 1 : 0.4 }}
          title={period === 'month' ? i18n.t('ak_home_next_month') : i18n.t('ak_home_next_day')}
        >
          ›
        </button>

      </div>
        {/* Підпис «індекси цін · дата» прибрано (дата вже видна в полі календаря).
          Лишився лише стан завантаження — його ніде більше не видно. */}
        {loading && (
          <span className="md:col-start-2 md:row-start-1 md:justify-self-end" style={{ fontSize: 11.5, color: 'var(--text-dim)', lineHeight: 1 }}>
            {i18n.t('ak_home_loading')}
          </span>
        )}
      </div>

      {/* ПЛИТКА — тільки самі індекси й підписи під ними. Навігація лишилася
          над плиткою, тож вкладених рамок усередині немає (§4). */}
      <div className="tile p-4 sm:p-5" style={{ display: 'grid', gap: 14 }}>

      {/* ІНДЕКСИ. За добу — чотири зони, як у них; за місяць бекенд дає одну
          агрегацію, тож і картка одна. */}
      <div className="grid grid-cols-2 xl:grid-cols-4" style={{ gap: 14 }}>
        <IndexCard id="rdn-day" label={i18n.t('ak_home_day_hr_8_23')} Icon={Sun} zone={zoneOf('day_8_23')}
          hint={period === 'month' && mzLoading ? i18n.t('ak_home_calculating') : undefined} />
        <IndexCard id="rdn-night" label={i18n.t('ak_home_night_hr_1_7_24')} Icon={Moon} zone={zoneOf('night')}
          hint={period === 'month' && mzLoading ? i18n.t('ak_home_calculating') : undefined} />
        <IndexCard id="rdn-peak" label={i18n.t('ak_home_peak_hr_18_23')} Icon={TrendingUp} zone={zoneOf('peak')}
          hint={period === 'month' && mzLoading ? i18n.t('ak_home_calculating') : undefined} />
        <IndexCard id="rdn-semipeak" label={i18n.t('ak_home_semi_peak_hr_8_17')} Icon={TrendingDown} zone={zoneOf('semipeak')}
          hint={period === 'month' && mzLoading ? i18n.t('ak_home_calculating') : undefined} />
      </div>

      {/* Якщо перерахунок завершився, але жодна зона не наповнилась, це не
          «немає даних», а збій запитів: dam-hourly відповів помилкою на всі
          дні (найімовірніше обмеження частоти при 5 паралельних запитах).
          Мовчазні прочерки в такому разі виглядали б як відсутність торгів,
          хоча торги були. */}
      {period === 'month' && !mzLoading && mz
        && Object.values(mz.zones).every((v) => v == null) && (
        <div style={{ fontSize: 11.5, color: 'var(--warn)' }}>
          {i18n.t('ak_home_failed_to_collect_hourly_trades_for_th')}
        </div>
      )}

      {period === 'month' && (
        // Середню за місяць від бекенда прибрано: вона середньозважена, а
        // зони тут середньоарифметичні. Два різні способи рахунку в одному
        // блоці дали б числа, які не сходяться між собою, і виглядало б це
        // як помилка, а не як дві методики.
        <div className="flex flex-wrap items-baseline" style={{
          gap: 8, paddingTop: 10, borderTop: '1px solid var(--line)', fontSize: 11.5,
        }}>
          <span style={{ color: 'var(--text-dim)' }}>
            {mz ? i18n.t('ak_home_arithmetic_mean_by_hour_days_with_data', { n: mz.days }) : i18n.t('ak_home_recalculating')}
          </span>
        </div>
      )}
      </div>
    </div>
  );
}
